import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib";
import { BlockError } from "@/shared/ui/block-error";
import { Skeleton } from "@/shared/ui/skeleton";

import { useAssets } from "../../api/useAssets";
import { useItemTooltips } from "../../api/useItemTooltips";
import { assetName } from "../../lib/assetName";
import { SoulsCost } from "./SoulsCost";
import { TooltipSection } from "./TooltipSection";

const SLOT = {
  weapon: {
    label: msg`Weapon`,
    text: cn("text-slot-weapon-text"),
    border: cn("border-slot-weapon"),
  },
  vitality: {
    label: msg`Vitality`,
    text: cn("text-slot-vitality-text"),
    border: cn("border-slot-vitality"),
  },
  spirit: {
    label: msg`Spirit`,
    text: cn("text-slot-spirit-text"),
    border: cn("border-slot-spirit"),
  },
} as const;

type ItemInfoCardProps = {
  itemId: number;
  // Заголовок — название; в шторке это заголовок диалога (`SheetTitle`).
  renderTitle: (name: string) => ReactNode;
  // Кнопка «Закрыть» справа от названия (во всплывающем окне).
  closeButton?: ReactNode;
  // Плашка под названием — зачем предмет здесь: «Ослабить врага · режет лечение».
  note?: ReactNode;
  // Строка внизу: «Берут 91 % сильных с этой сборкой · в среднем 5:58».
  footer?: ReactNode;
};

// Описание предмета, как в магазине игры (макет «19г» `design/prototype/ItemCard.dc.html`):
// иконка, название, ветка и цена в душах, плашка «зачем» (страница героя, «19д»),
// «Активный · перезарядка», свойства и эффекты.
export const ItemInfoCard = ({
  itemId,
  renderTitle,
  closeButton,
  note,
  footer,
}: ItemInfoCardProps) => {
  const { i18n, t } = useLingui();
  const { data: assets } = useAssets();
  const tooltips = useItemTooltips(true);
  const item = assets?.items.find((entry) => entry.id === itemId);
  const tooltip = tooltips.data?.items.find((entry) => entry.id === itemId);
  const name = item === undefined ? t`Item #${itemId}` : assetName(item.name);
  const slot = item?.slot == null ? null : SLOT[item.slot];
  const cooldown =
    tooltip?.cooldown_s == null
      ? null
      : new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 1 }).format(tooltip.cooldown_s);

  return (
    <div className="flex flex-col gap-3 text-left">
      <div className="flex items-start gap-3">
        {item?.icon_url != null && (
          <img
            src={item.icon_url}
            alt=""
            width={52}
            height={52}
            style={{ width: 52, height: 52 }}
            className={cn(
              "shrink-0 rounded-[10px] border-[3px] bg-surface-raised object-cover",
              slot?.border,
            )}
          />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {renderTitle(name)}
          <span className="flex flex-wrap items-center gap-x-1.5 text-[13px] text-muted">
            {slot !== null && (
              <span className={cn("font-semibold", slot.text)}>{i18n._(slot.label)}</span>
            )}
            {slot !== null && tooltip?.cost != null && <span aria-hidden="true">·</span>}
            {tooltip?.cost != null && <SoulsCost cost={tooltip.cost} />}
          </span>
        </div>
        {closeButton}
      </div>

      {note !== undefined && (
        <p className="self-start rounded-md bg-surface-raised px-2.5 py-1 text-[12.5px] text-foreground">
          {note}
        </p>
      )}

      {tooltips.isPending && (
        <div className="flex flex-col gap-2" aria-busy="true">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      )}
      {tooltips.isError && (
        <BlockError
          title={<Trans>Could not load the description</Trans>}
          retrying={tooltips.isFetching}
          onRetry={() => void tooltips.refetch()}
          className="px-3 py-3"
        />
      )}
      {tooltips.isSuccess && tooltip === undefined && (
        <p className="text-sm text-muted">
          <Trans>No description for this item yet</Trans>
        </p>
      )}

      {tooltip?.active === true && (
        <span className="self-start rounded-full border border-border-strong px-2.5 py-0.5 text-xs text-accent-strong">
          {cooldown === null ? (
            <Trans>Active</Trans>
          ) : (
            <Trans>Active · cooldown {cooldown} s</Trans>
          )}
        </span>
      )}
      {tooltip?.sections.map((section, index) => (
        <TooltipSection key={`${section.kind}-${index}`} section={section} />
      ))}

      {footer !== undefined && (
        <p className="border-t border-border pt-2.5 text-xs text-subtle">{footer}</p>
      )}
    </div>
  );
};
